import { createContext, useContext, useEffect, useState, useCallback } from "react";
// import { fetchTranscripts } from "../api/transcriptService";
import { processCallData } from "../api/callData";
import { useCustomer } from "./CustomerContext";
import { useDataAccess } from "../hooks/useDataAccess";

// Mock data fallback for when API fails
// const getMockDataForCustomer = (customerId) => {
//   const baseMockData = [
//     {
//       id: `${customerId}_call_1`,
//       caller: '+1234567890',
//       duration: '2:30',
//       sentiment: 'positive',
//       timestamp: new Date().toISOString(),
//       summary: `Sample call for ${customerId}`,
//       transcript: 'This is a sample transcript for development purposes.'
//     }
//   ];

//   if (customerId === 'wards') {
//     return [
//       {
//         id: 'wards_call_1',
//         caller: '+1555123456',
//         duration: '3:45',
//         sentiment: 'positive',
//         timestamp: new Date(Date.now() - 86400000).toISOString(), // Yesterday
//         summary: 'Customer inquiry about rental equipment availability',
//         transcript: 'Customer called asking about excavator rental rates and availability for next week.'
//       },
//       {
//         id: 'wards_call_2',
//         caller: '+1555987654',
//         duration: '1:20',
//         sentiment: 'neutral',
//         timestamp: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
//         summary: 'Equipment return scheduling',
//         transcript: 'Customer calling to schedule return of rented bulldozer.'
//       }
//     ];
//   }

//   return baseMockData;
// };

export const CallDataContext = createContext();

export const CallDataProvider = ({ children }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { currentCustomer, getEffectiveCustomerId, isImpersonating } = useCustomer();
  const { makeCustomerApiRequest } = useDataAccess();

 
  const fetchData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      const customerId = getEffectiveCustomerId();
      // console.log("🔍 CallDataContext: Fetching data for customer:", customerId, forceRefresh ? "(FORCED REFRESH)" : "");

      // 1. If we don't have a valid customer ID yet, it means the CustomerContext is still loading.
      //    Set data to empty and wait for the context to provide a valid ID.
      if (!customerId || customerId === 'unknown') {
        // console.log("CallDataContext: Waiting for a valid customer context. Clearing data for now.");
        setData([]);
        setLoading(false);
        return; // Exit early
      }

      // 2. Always use the modern, customer-scoped API request.
      // console.log("🚀 CallDataContext: Using customer-scoped API for:", customerId);
      
      // Add a cache-busting parameter when doing a forced refresh
      const endpoint = forceRefresh ? 
        `calls?_cache_buster=${Date.now()}` : 
        'calls';
        
      const response = await makeCustomerApiRequest(endpoint);
      // console.log("✅ CallDataContext: Customer API response received:", response);

      // 3. The new API returns an object like { calls: [...] }.
      //    We need to extract the 'calls' array from the response.
      const callsArray = response?.calls || [];

      if (Array.isArray(callsArray)) {
        const processedData = callsArray.map(processCallData);
        // console.log("CallDataContext: Processed customer data:", processedData, 
        // "for customer:", customerId);
        setData(processedData);
      } else {
        console.warn("CallDataContext: API response did not contain a 'calls' array.", response);
        setData([]);
      }

    } catch (err) {
      console.error("CallDataContext: Failed to load call data:", err);
      setError(err);
      setData([]); // Clear data on error
    } finally {
      setLoading(false);
    }
  }, [getEffectiveCustomerId, makeCustomerApiRequest]); // Removed currentCustomer?.id to avoid stale closures

  const updateCall = (callId, updatedCall) => {
    setData(currentData =>
      currentData.map(call =>
        call.id === callId ? { ...call, ...updatedCall } : call
      )
    );
  };

  // 3. The refresh function now simply calls fetchData
  const refreshData = useCallback(() => {
    // Explicitly set loading to true for user-initiated refresh
    setLoading(true);
    fetchData();
  }, [fetchData]);

  // Effect for the initial data load and when customer changes
  useEffect(() => {
    // console.log("🔄 CallDataContext useEffect triggered");
    // console.log("🔄 Current customer:", currentCustomer?.id);
    // console.log("🔄 Is impersonating:", isImpersonating);
    
    // Fetch immediately when customer changes - the fetchData callback is already properly memoized
    fetchData();
  }, [fetchData, currentCustomer?.id, isImpersonating]); // Directly track customer ID for more reliable reactivity
  
  // Listen for force data refresh events
  useEffect(() => {
    const handleForceDataRefresh = (event) => {
      // console.log("🔄 CallDataContext: Force data refresh event received", event.detail);
      
      // Use the forceRefresh parameter to bypass caching and force a clean fetch
      fetchData(true).then(() => {
        // Clear the force refresh flag after successful refresh
        sessionStorage.removeItem('force_data_refresh');
        // console.log("✅ CallDataContext: Forced data refresh completed");
      });
    };
    
    // Add event listener
    window.addEventListener('forceDataRefresh', handleForceDataRefresh);
    
    // Check for force refresh flag on mount/update
    const shouldForceRefresh = sessionStorage.getItem('force_data_refresh') === 'true';
    if (shouldForceRefresh) {
      // console.log("🔄 CallDataContext: Force refresh flag detected on mount");
      handleForceDataRefresh({ detail: { source: 'mount', timestamp: Date.now() } });
    }
    
    // Cleanup
    return () => {
      window.removeEventListener('forceDataRefresh', handleForceDataRefresh);
    };
  }, [fetchData]);

  // Auto-polling removed - data will only refresh via manual refresh button

  // 6. Removed 'loadMore' and 'more' from the provider value
  return (
    <CallDataContext.Provider value={{ data, loading, error, refreshData, updateCall, setData }}>
      {children}
    </CallDataContext.Provider>
  );
};

export const useCallData = () => useContext(CallDataContext);
